import { oc } from '@orpc/contract';
import { z } from 'zod';
import { Id, OkResponse, Timestamp } from './common.js';
import { Code } from './meta.js';
/** Видимость записи каталога (docs/PLAN.md, раздел 2, п. 18). */
export const Visibility = z.enum(['public', 'members', 'private']);
/**
 * Значения полей записи в «формате формы»: ключ — код поля из метаданных
 * (колонки, attributes и связи вместе). Схему проверяет сервер по метаданным типа.
 */
export const FieldValues = z.record(z.string(), z.unknown());
export const ImageRole = z.enum(['obverse', 'reverse', 'edge', 'slab', 'other']);
const Fraction = z.number().min(0).max(1);
/** Недеструктивная правка изображения: оригинал не меняется, варианты пересобираются. */
export const ImageTransform = z.object({
    rotate: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]),
    /** Кроп в долях от размера оригинала (до поворота); поворот применяется после кропа. */
    crop: z
        .object({ x: Fraction, y: Fraction, w: Fraction.min(0.02), h: Fraction.min(0.02) })
        .refine((c) => c.x + c.w <= 1.0001 && c.y + c.h <= 1.0001, 'crop_bounds')
        .nullable(),
});
export const MediaSource = z.enum(['own', 'numista']);
export const CatalogImage = z.object({
    id: Id,
    mediaId: Id,
    role: ImageRole,
    sortKey: z.string(),
    transform: ImageTransform,
    width: z.number().int(),
    height: z.number().int(),
    source: MediaSource,
    attribution: z.string().nullable(),
    urls: z.object({ thumb: z.string(), display: z.string(), original: z.string() }),
});
export const CatalogVariant = z.object({
    id: Id,
    label: z.string(),
    sortKey: z.string(),
    values: FieldValues,
    version: z.number().int(),
});
export const CatalogItem = z.object({
    id: Id,
    typeCode: Code,
    visibility: Visibility,
    ownerId: Id.nullable(),
    values: FieldValues,
    variants: z.array(CatalogVariant),
    images: z.array(CatalogImage),
    /**
     * Оригиналы полей, переведённых при импорте из Numista (код поля → текст на языке Numista).
     * Анониму не отдаются, как и остальные тексты Numista.
     */
    originals: z.record(z.string(), z.string()),
    version: z.number().int(),
    createdAt: Timestamp,
    updatedAt: Timestamp,
    /** Может ли текущий пользователь править запись. */
    canEdit: z.boolean(),
});
export const CatalogListItem = z.object({
    id: Id,
    typeCode: Code,
    visibility: Visibility,
    title: z.string(),
    values: FieldValues,
    thumb: z.string().nullable(),
    /**
     * Моя коллекция (для вошедшего; у анонима — null): сколько штук у меня есть (статусы
     * «в наличии»: в коллекции, на продаже, на обмен) и стоит ли запись в «В поиске».
     */
    collection: z.object({ owned: z.number().int(), wanted: z.boolean() }).nullable(),
});
export const CatalogSort = z.enum(['relevance', 'title', 'year', 'recent']);
export const CatalogListInput = z.object({
    q: z.string().trim().max(200).optional(),
    type: Code.optional(),
    /** FilterSpec в JSON (передаётся строкой в query). */
    filter: z.string().max(10_000).optional(),
    sort: CatalogSort.optional(),
    cursor: z.string().max(500).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    /** Только мои приватные записи. */
    mine: z.stringbool().optional(),
    /** Есть ли запись в моей коллекции (статусы «в наличии»). */
    owned: z.enum(['yes', 'no']).optional(),
});
export const ValidationIssue = z.object({
    path: z.array(z.union([z.string(), z.number()])),
    message: z.string(),
});
export const catalogErrors = {
    NOT_FOUND: { status: 404, message: 'not_found' },
    VERSION_CONFLICT: { status: 409, message: 'version_conflict' },
    FORBIDDEN: { status: 403, message: 'forbidden' },
    VALIDATION_FAILED: {
        status: 400,
        message: 'validation_failed',
        data: z.object({ issues: z.array(ValidationIssue) }),
    },
};
export const MediaUpload = z.object({
    id: Id,
    width: z.number().int(),
    height: z.number().int(),
    bytes: z.number().int(),
    mime: z.string(),
    /** Такой файл уже был загружен — возвращена существующая запись. */
    deduplicated: z.boolean(),
});
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
/** Запись журнала изменений общего каталога (docs/PLAN.md, этап 3). */
export const CatalogChange = z.object({
    id: Id,
    catalogItemId: Id,
    itemTitle: z.string(),
    /** create, update, visibility, delete, import, variant_*, image_add, image_remove. */
    action: z.string(),
    /** { поле: [было, стало] } */
    changes: z.record(z.string(), z.unknown()),
    user: z.object({ id: Id, name: z.string() }).nullable(),
    createdAt: Timestamp,
});
/** Результат массовой операции: сколько изменено и что не прошло проверку. */
export const BulkResult = z.object({
    updated: z.number().int(),
    failed: z.array(z.object({ id: Id, issues: z.array(ValidationIssue) })),
});
export const catalogContract = {
    list: oc
        .route({ method: 'GET', path: '/catalog/items' })
        .errors({ VALIDATION_FAILED: catalogErrors.VALIDATION_FAILED })
        .input(CatalogListInput)
        .output(z.object({ items: z.array(CatalogListItem), nextCursor: z.string().nullable() })),
    get: oc
        .route({ method: 'GET', path: '/catalog/items/{id}' })
        .errors({ NOT_FOUND: catalogErrors.NOT_FOUND })
        .input(z.object({ id: Id }))
        .output(CatalogItem),
    create: oc
        .route({ method: 'POST', path: '/catalog/items' })
        .errors(catalogErrors)
        .input(z.object({ typeCode: Code, visibility: Visibility, values: FieldValues }))
        .output(CatalogItem),
    update: oc
        .route({ method: 'PATCH', path: '/catalog/items/{id}' })
        .errors(catalogErrors)
        .input(z.object({ id: Id, version: z.number().int(), values: FieldValues }))
        .output(CatalogItem),
    setVisibility: oc
        .route({ method: 'PATCH', path: '/catalog/items/{id}/visibility' })
        .errors(catalogErrors)
        .input(z.object({ id: Id, visibility: Visibility }))
        .output(CatalogItem),
    remove: oc
        .route({ method: 'DELETE', path: '/catalog/items/{id}' })
        .errors({
        NOT_FOUND: catalogErrors.NOT_FOUND,
        VALIDATION_FAILED: catalogErrors.VALIDATION_FAILED,
    })
        .input(z.object({ id: Id }))
        .output(OkResponse),
    /** Массовое редактирование: одно значение поля и/или видимость для выбранных записей. */
    bulk: oc
        .route({ method: 'POST', path: '/catalog/items/bulk' })
        .errors(catalogErrors)
        .input(z.object({
        ids: z.array(Id).min(1).max(500),
        visibility: Visibility.exclude(['private']).optional(),
        values: FieldValues.optional(),
    }))
        .output(BulkResult),
    history: oc
        .route({ method: 'GET', path: '/catalog/items/{id}/history' })
        .errors({ NOT_FOUND: catalogErrors.NOT_FOUND, FORBIDDEN: catalogErrors.FORBIDDEN })
        .input(z.object({ id: Id }))
        .output(z.array(CatalogChange)),
    changes: oc
        .route({ method: 'GET', path: '/catalog/changes' })
        .errors({ FORBIDDEN: catalogErrors.FORBIDDEN })
        .input(z.object({
        cursor: z.string().max(100).optional(),
        limit: z.coerce.number().int().min(1).max(200).optional(),
    }))
        .output(z.object({ items: z.array(CatalogChange), nextCursor: z.string().nullable() })),
    variants: {
        create: oc
            .route({ method: 'POST', path: '/catalog/items/{itemId}/variants' })
            .errors(catalogErrors)
            .input(z.object({ itemId: Id, label: z.string().trim().min(1).max(200), values: FieldValues }))
            .output(CatalogVariant),
        update: oc
            .route({ method: 'PATCH', path: '/catalog/variants/{id}' })
            .errors(catalogErrors)
            .input(z.object({
            id: Id,
            version: z.number().int(),
            label: z.string().trim().min(1).max(200).optional(),
            values: FieldValues.optional(),
        }))
            .output(CatalogVariant),
        remove: oc
            .route({ method: 'DELETE', path: '/catalog/variants/{id}' })
            .errors({ NOT_FOUND: catalogErrors.NOT_FOUND })
            .input(z.object({ id: Id }))
            .output(OkResponse),
    },
    images: {
        attach: oc
            .route({ method: 'POST', path: '/catalog/items/{itemId}/images' })
            .errors({ NOT_FOUND: catalogErrors.NOT_FOUND })
            .input(z.object({ itemId: Id, mediaId: Id, role: ImageRole }))
            .output(CatalogImage),
        update: oc
            .route({ method: 'PATCH', path: '/images/{id}' })
            .errors({ NOT_FOUND: catalogErrors.NOT_FOUND })
            .input(z.object({
            id: Id,
            role: ImageRole.optional(),
            sortKey: z.string().max(64).optional(),
            transform: ImageTransform.optional(),
        }))
            .output(CatalogImage),
        remove: oc
            .route({ method: 'DELETE', path: '/images/{id}' })
            .errors({ NOT_FOUND: catalogErrors.NOT_FOUND })
            .input(z.object({ id: Id }))
            .output(OkResponse),
    },
};
export const mediaContract = {
    /** Загрузка файла (multipart). Дубликат по sha256 возвращает существующую запись. */
    upload: oc
        .route({ method: 'POST', path: '/media' })
        .errors({
        UNSUPPORTED_FORMAT: { status: 415, message: 'unsupported_format' },
        FILE_TOO_LARGE: { status: 413, message: 'file_too_large' },
    })
        .input(z.object({ file: z.file() }))
        .output(MediaUpload),
};
//# sourceMappingURL=catalog.js.map